import { AnyAction } from 'redux';
import {
  Anrede,
  Domaenenwert,
  FindProgrammteilnahmeBFFResponseModel,
  KommunikationsArt,
  ProgrammteilnahmeEmailFieldBehaviour,
  ProgrammteilnahmeStatus,
  Tarifbereich,
  Verwendungszweck,
} from '@ps20/bff-models';
import { GlobalActions } from '@ps20-frontend/shop-integration-redux';
import {
  convertToKommDaten,
  determineAssekuranztarifFromTarifbereich,
  formatIban,
  KommDaten,
  KommDatenEmail,
} from '@ps20-frontend/frontend-models';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { INITIAL_STATE } from '../initialState';
import { CheckoutActions } from '../checkout.actions';
import { KommdatenChanged } from '../action-types/kommdatenChanged';

export interface Versicherungsnehmer {
  kundennummer: string;
  kundenverwaltungID: string;
  anrede: Anrede;
  vorname: string;
  nachname: string;
  firmenname: string;
  juristisch: boolean;
  businessForm: Domaenenwert;
  geburtsdatum: string;
  isAssekuranztarif: boolean;
  tarifbereich?: Tarifbereich;

  plz: string;
  plzValid: boolean;
  ort: string;
  ortsteil?: string;
  strasse: string;
  hausnummer: string;
  hausnrzus?: string;
  adresszusatz?: string;
  adresszusatzkennzeichen?: Domaenenwert;
  land?: Domaenenwert;

  namenszusatz?: Domaenenwert;
  akadgrad?: Domaenenwert;
  anrtitel?: Domaenenwert;
  famstand?: Domaenenwert;
  staatsbschaft: Domaenenwert;
  gebname?: string;
  heimatort?: string;
  adelsbez?: Domaenenwert;
  adelstitel?: Domaenenwert;
  geburtsland?: Domaenenwert;
  email?: KommDatenEmail;
  mobil: KommDaten;
  telefon: KommDaten;
  fax?: KommDaten;
  vnVorbelegt: boolean;
  bankverbindungen: Bankverbindung[];
  adressvorschlag: Adressvorschlag;
  addressInvalid?: boolean;
  vnLoadedFromSavedAntrag?: boolean;
  kdvKommunikationsdaten?: KommDaten[];
}

export interface Bankverbindung {
  iban: string;
  bic?: string;
  bankname?: string;
  kontonummer?: string;
  blz?: string;
  verwendungszweck?: Verwendungszweck;
  bankOrt: string;
  sepamandatErteilt: boolean;
}

export interface Adressvorschlag {
  orte: string[];
  strassen: string[];
  lastError?: boolean;
}

function transformGeburtsdatum(action: AnyAction): string {
  const geburtsdatum = get(action.payload, 'versicherungsnehmer.geburtsdatum', null);
  return formatGeburtsdatum(geburtsdatum);
}

export function formatGeburtsdatum(unformattedDateString: string): string {
  if (unformattedDateString) {
    const dateSplitted = unformattedDateString.split('-');
    unformattedDateString = `${dateSplitted[2]}.${dateSplitted[1]}.${dateSplitted[0]}`;
  }
  return unformattedDateString;
}

function getKomm(action: AnyAction, kommArt: string): KommDaten {
  const kommDaten: KommDaten = {
    vorbelegt: false,
    wert: '',
    kommunikationsArt: undefined,
    verwendung: [],
    herkunft: null,
    angabeStatus: null,
  };
  const kommunikationsDaten = get(action.payload, 'versicherungsnehmer.kommunikationsdaten', []);
  const kommunikation =
    kommunikationsDaten &&
    kommunikationsDaten.find((kommdaten: KommDaten) => kommdaten.kommunikationsArt === kommArt);
  if (kommunikation) {
    return convertToKommDaten(kommunikation);
  }

  return kommDaten;
}

function getKommEmail(action: AnyAction): KommDatenEmail {
  const kommEmailBasics: KommDaten = getKomm(action, KommunikationsArt.EMAIL);

  return {
    ...kommEmailBasics,
    fieldBehaviour: ProgrammteilnahmeEmailFieldBehaviour.OFFEN,
    changedByUser: false,
  };
}

export const versicherungsnehmerReducer = function (
  sectionState: Versicherungsnehmer = null,
  action: AnyAction,
): Versicherungsnehmer {
  switch (action.type) {
    case CheckoutActions.VERSICHERUNGSNEHMER_ANREDE_CHANGED: {
      return { ...sectionState, anrede: action.payload.anrede };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_VORNAME_CHANGED: {
      return { ...sectionState, vorname: action.payload.vorname };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_NACHNAME_CHANGED: {
      return { ...sectionState, nachname: action.payload.nachname };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_FIRMENNAME_CHANGED: {
      return { ...sectionState, firmenname: action.payload.firmenname };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_GEBURTSDATUM_CHANGED: {
      return {
        ...sectionState,
        geburtsdatum: action.payload.geburtsdatum,
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_PLZ_CHANGED: {
      return { ...sectionState, plz: action.payload.plz };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_PLZ_VALID: {
      const newSectionState = cloneDeep(sectionState);
      newSectionState.plzValid = true;
      newSectionState.adressvorschlag = action.payload;
      newSectionState.ort = '';
      if (action.payload.orte?.length === 1) {
        newSectionState.ort = action.payload.orte[0];
      }
      newSectionState.strasse = '';
      if (action.payload.strassen?.length === 1) {
        newSectionState.strasse = action.payload.strassen[0];
      }
      newSectionState.adressvorschlag.lastError = false;
      return newSectionState;
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_PLZ_INVALID: {
      return {
        ...sectionState,
        plzValid: false,
        adressvorschlag: {
          ...sectionState.adressvorschlag,
          lastError: false,
        },
      };
    }
    case CheckoutActions.VALIDIEREPLZ_SERVICE_DOWN: {
      return {
        ...sectionState,
        adressvorschlag: {
          ...sectionState.adressvorschlag,
          lastError: true,
        },
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_ORT_CHANGED: {
      return { ...sectionState, ort: action.payload.ort };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_STRASSE_CHANGED: {
      return { ...sectionState, strasse: action.payload.strasse };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_HAUSNUMMER_CHANGED: {
      return { ...sectionState, hausnummer: action.payload.hausnummer };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_EMAIL_CHANGED: {
      const actionPayload: KommdatenChanged = action.payload;

      return {
        ...sectionState,
        email: {
          ...sectionState.email,
          wert: actionPayload.value,
          vorbelegt: false,
          angabeStatus: null,
          changedByUser: true,
        },
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_EMAIL_ANGABENSTATUS_CHANGED: {
      const actionPayload: KommdatenChanged = action.payload;

      return {
        ...sectionState,
        email: {
          ...sectionState.email,
          angabeStatus: actionPayload.angabeStatus,
        },
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_MOBIL_CHANGED: {
      const actionPayload: KommdatenChanged = action.payload;

      return {
        ...sectionState,
        mobil: {
          ...sectionState.mobil,
          laendervorwahl: actionPayload.laendervorwahl,
          wert: actionPayload.value,
          angabeStatus: null,
          vorbelegt: false,
        },
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_MOBIL_ANGABENSTATUS_CHANGED: {
      const actionPayload: KommdatenChanged = action.payload;

      return {
        ...sectionState,
        mobil: {
          ...sectionState.mobil,
          angabeStatus: actionPayload.angabeStatus,
        },
      };
    }
    case CheckoutActions.VERSICHERUNGSNEHMER_TELEFON_CHANGED: {
      const actionPayload: KommdatenChanged = action.payload;

      return {
        ...sectionState,
        telefon: {
          ...sectionState.telefon,
          laendervorwahl: actionPayload.laendervorwahl,
          wert: actionPayload.value,
        },
      };
    }
    case GlobalActions.EINSTIEG_REQUEST_SUCCESSFUL: {
      const newState = sectionState;
      const anredeValue = get(action.payload, 'versicherungsnehmer.anrede', null);

      if (anredeValue === 'HERR') {
        newState.anrede = Anrede.HERR;
      } else if (anredeValue === 'FIRMA') {
        newState.anrede = Anrede.FIRMA;
      } else {
        newState.anrede = Anrede.FRAU;
      }
      newState.kundenverwaltungID = get(
        action.payload,
        'versicherungsnehmer.kundenverwaltungID',
        null,
      );
      newState.kundennummer = get(action.payload, 'versicherungsnehmer.kundennummer', null);
      newState.vorname = get(action.payload, 'versicherungsnehmer.vorname', null);
      newState.nachname = get(action.payload, 'versicherungsnehmer.nachname', null);
      newState.firmenname = get(action.payload, 'versicherungsnehmer.firmenname', null);
      newState.juristisch = get(action.payload, 'versicherungsnehmer.juristisch', null);
      newState.businessForm = get(action.payload, 'versicherungsnehmer.businessForm', null);
      newState.geburtsdatum = transformGeburtsdatum(action);
      newState.namenszusatz = get(action.payload, 'versicherungsnehmer.namenszusatz', null);
      newState.akadgrad = get(action.payload, 'versicherungsnehmer.akadgrad', null);
      newState.anrtitel = get(action.payload, 'versicherungsnehmer.anrtitel', null);
      newState.staatsbschaft = get(action.payload, 'versicherungsnehmer.staatsbschaft', null);
      newState.gebname = get(action.payload, 'versicherungsnehmer.gebname', null);
      newState.heimatort = get(action.payload, 'versicherungsnehmer.heimatort', null);
      newState.adelsbez = get(action.payload, 'versicherungsnehmer.adelsbez', null);
      newState.adelstitel = get(action.payload, 'versicherungsnehmer.adelstitel', null);
      newState.geburtsland = get(action.payload, 'versicherungsnehmer.geburtsland', null);
      newState.famstand = get(action.payload, 'versicherungsnehmer.famstand', null);
      newState.vnVorbelegt = !!get(action.payload, 'versicherungsnehmer.adresse.plz', null);

      newState.plz = get(action.payload, 'versicherungsnehmer.adresse.plz', null);
      newState.ort = get(action.payload, 'versicherungsnehmer.adresse.ort', null);
      newState.ortsteil = get(action.payload, 'versicherungsnehmer.adresse.ortsteil', null);
      newState.strasse = get(action.payload, 'versicherungsnehmer.adresse.strasse', null);
      newState.hausnummer = get(action.payload, 'versicherungsnehmer.adresse.hausnummer', null);
      newState.hausnrzus = get(action.payload, 'versicherungsnehmer.adresse.hausnrzus', null);
      newState.adresszusatz = get(action.payload, 'versicherungsnehmer.adresse.adresszusatz', null);
      newState.adresszusatzkennzeichen = get(
        action.payload,
        'versicherungsnehmer.adresse.adresszusatzkennzeichen',
        null,
      );
      newState.land = get(action.payload, 'versicherungsnehmer.adresse.land', null);

      newState.email = getKommEmail(action);
      newState.mobil = getKomm(action, 'HANDY');
      newState.telefon = getKomm(action, 'TELEFON');
      newState.fax = getKomm(action, 'FAX');
      newState.kdvKommunikationsdaten = get(
        action.payload,
        'versicherungsnehmer.kommunikationsdaten',
        [],
      );

      newState.bankverbindungen = get(
        action.payload,
        'versicherungsnehmer.bankverbindungen',
        INITIAL_STATE.versicherungsnehmer.bankverbindungen,
      );

      if (newState.bankverbindungen != null) {
        newState.bankverbindungen.forEach(bankverbindung => {
          bankverbindung.iban = formatIban(bankverbindung.iban);
        });
      }

      return newState;
    }
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA_AFTER_VV_LOAD:
    case GlobalActions.UPDATE_ASSEKURANZTARIF_DATA: {
      return {
        ...sectionState,
        isAssekuranztarif: action.payload,
      };
    }
    case GlobalActions.TARIFBEREICH_CHANGED: {
      return {
        ...sectionState,
        tarifbereich: action.payload,
        isAssekuranztarif: determineAssekuranztarifFromTarifbereich(action.payload),
      };
    }
    case CheckoutActions.PROGRAMMTEILNAHME_SUCCESS: {
      const programmteilnahmeBFFResponseModel: FindProgrammteilnahmeBFFResponseModel =
        action.payload;
      if (
        programmteilnahmeBFFResponseModel.programmteilnahmeEmail != null &&
        !sectionState.email.changedByUser
      ) {
        sectionState.email.changedByProgrammteilnahme =
          sectionState.email.wert !== programmteilnahmeBFFResponseModel.programmteilnahmeEmail;
        sectionState.email.wert = programmteilnahmeBFFResponseModel.programmteilnahmeEmail;
        sectionState.email.vorbelegt = true;
      } else {
        sectionState.email.changedByProgrammteilnahme = false;
      }
      sectionState.email.programmName = programmteilnahmeBFFResponseModel.programmname;

      sectionState.email.programmStatus = programmteilnahmeBFFResponseModel.programmteilnahmeStatus;
      sectionState.email.fieldBehaviour =
        programmteilnahmeBFFResponseModel.programmteilnahmeEmailFieldBehaviour;

      return checkEmailConsideringContradiction(sectionState);
    }
    case GlobalActions.EINSTIEG_WITH_INVALID_ADDRESS: {
      return {
        ...sectionState,
        addressInvalid: true,
      };
    }
    default:
      return sectionState;
  }

  function checkEmailConsideringContradiction(
    versicherungsnehmer: Versicherungsnehmer,
  ): Versicherungsnehmer {
    const result = { ...versicherungsnehmer };

    const emailData = versicherungsnehmer.email;

    if (!emailData.wert) {
      result.email = {
        ...emailData,
        wert: '',
        fieldBehaviour: ProgrammteilnahmeEmailFieldBehaviour.OFFEN,
      };
      return result;
    }

    if (emailData.programmStatus !== ProgrammteilnahmeStatus.WIDERSPROCHEN) {
      result.email = {
        ...emailData,
        fieldBehaviour: emailData.fieldBehaviour || ProgrammteilnahmeEmailFieldBehaviour.EDITIERBAR,
      };
      return result;
    }

    const alternativeEmails =
      versicherungsnehmer.kdvKommunikationsdaten?.filter(
        item => item.kommunikationsArt === KommunikationsArt.EMAIL && item.wert !== emailData.wert,
      ) || [];

    if (alternativeEmails.length > 0) {
      result.email = {
        ...emailData,
        wert: alternativeEmails[0].wert,
        fieldBehaviour: ProgrammteilnahmeEmailFieldBehaviour.EDITIERBAR,
      };
    } else {
      result.email = {
        ...emailData,
        wert: '',
        fieldBehaviour: ProgrammteilnahmeEmailFieldBehaviour.OFFEN,
      };
    }

    return result;
  }
};
